import {AgGridReact} from "ag-grid-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './DiagnosticStyles.css';
import {calculateRoundPercent, cellStylePercentage, PercentageCell, TimeCell} from './Utils.js';
import {
    ChartLineColor,
    COL_DEFS,
    defaultPDFParams,
    GRID_ENUMS,
} from "./Styles";
// import { Line } from '@ant-design/plots';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {printDoc} from "./pdfExport/printDoc";
import CircularLoader from "../common/CircularLoader";
import Tooltip from "../common/Tooltip";
import IconButton from "../common/IconButton";
import LineChartLegend, {LEGEND_SHAPES} from "./LineChartLegend";
import ScatterChartComponent from "./ScatterChartComponent";
import {TEST_GROUP_TEST_TYPES, TIME_FILTER, LOCATION_AGGREGATE} from "../tests/teacher/Diagnostics/Reports/TestGroupReports";

const ScoreVsTime = props => {
    const {isLoading, setLoading, testAssignmentsData, students, studentIds, selectedTestIds,
            selectedDifficultyIds,
            showTimeLine, showScoreLine,
            openDialogCallback, triggerGenerate,
            studentTimeFilter,
            timeTakenAggregate,
            scoreAverageAggregate,
            // timeTakenSelect,
            // averageScoreSelect
        } = props,
        preTestPercentFieldName = "preTestScorePercent",
        postTestPercentFieldName = "postTestScorePercent",
        preTestTimeTakenFieldName = "preTestTimeTaken",
        postTestTimeTakenFieldName = "postTestTimeTaken",
        postTest = 'Post Test',
        preTest = 'Pre Test',
        rowHeight = 30,
        headerHeight = 32,
        REPORT_NAME = 'ScoreVsTimeReport';

    const labelDisplayMap = {
        value:'% correct',
        timeTakenMins:'mins',
        timeTakenSeconds:'secs',
    }

    const StudentCustomCell = props => {
        const {data, value} = props;
        const onClick = () => {
            if (typeof data.callback === 'function') {
                data.callback(data.studentId);
            }
        }

        return (
            <div className={'custom-cell-cont'}>
                <Tooltip title={value} placement="left" enterNextDelay={100}>
                    <div className={'label'}>{value}</div>
                </Tooltip>
                <IconButton onClick={onClick} tooltip={'Show Student Test/s'} placement={'left'}>
                    <span className="material-icons">open_in_new</span>
                </IconButton>
            </div>
        )
    }

    const columnDefs = [
        {
            flex:1,
            headerName:'Student',
            headerClass: 'text-center-align',
            cellStyle:{
                textAlign:'left',
                borderRight:'1px solid lightgrey'
            },
            field:'name',
            sortable:true,
            unSortIcon: true,
            wrapText:true,
            cellRenderer: StudentCustomCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName:'Pre/mid-test(s) score',
            field: preTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: 'Pre/mid-test time taken',
            field: preTestTimeTakenFieldName,
            headerClass: 'text-center-align',
            cellStyle: {textAlign: 'center'},
            cellRenderer: TimeCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: 'Post-test score',
            field: postTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: 'Post-test time taken',
            field: postTestTimeTakenFieldName,
            headerClass: 'text-center-align',
            cellStyle: {textAlign: 'center'},
            cellRenderer: TimeCell
        }
    ];

    const growthLegendData = [
        {
            text:'Pre/mid-test', colors:{
                background: ChartLineColor.PRE_TEST,
                border:ChartLineColor.PRE_TEST
            }, shape: LEGEND_SHAPES.CIRCLE,
        },
        {
            text:'Post-test', colors:{
                background: ChartLineColor.POST_TEST,
                border:ChartLineColor.POST_TEST
            },
            shape: LEGEND_SHAPES.CIRCLE
        },
        {
            text:'Average score', colors:{
                background: ChartLineColor.AVERAGE_SCORE_LINE,
                border:ChartLineColor.AVERAGE_SCORE_LINE
            }, shape: LEGEND_SHAPES.DASH
        },
        {
            text:'Average time taken', colors:{
                background: ChartLineColor.AVERAGE_TIME_LINE,
                border:ChartLineColor.AVERAGE_TIME_LINE
            }, shape: LEGEND_SHAPES.DASH
        }
        // ,{
        //     text:'Diamond test', colors:{
        //         background: ChartLineColor.MEAN,
        //         border:ChartLineColor.MEAN
        //     }, shape: LEGEND_SHAPES.DIAMOND
        // }
    ]



    // const hideAverageLine = (averageScoreSelect === LOCATION_AGGREGATE.CLASS.val && scoreAverageAggregate === null),
    //     hideTimeTakenLine = (timeTakenSelect === LOCATION_AGGREGATE.CLASS.val && timeTakenAggregate === null);

    const gridRef = useRef(),
        [graphData,setGraphData] = useState(null),
        [chartData,setChartData] = useState(null),
        referenceLineXColor = ChartLineColor.AVERAGE_TIME_LINE,
        referenceLineYColor = ChartLineColor.AVERAGE_SCORE_LINE,
        [referenceLineXValue, setReferenceLineXValue] = useState(0),
        [referenceLineYValue, setReferenceLineYValue] = useState(0),
        [selectedAverageTimeTaken, setSelectedAverageTimeTaken] = useState(null),
        [selectedAverageScore, setSelectedAverageScore] = useState(null),
        [studentSeriesMap,setStudentSeriesMap] = useState({}),
        [unfilteredData, setUnfilteredData] = useState([]),
        [unfilteredChartData, setUnfilteredChartData] = useState([]),
        [questionResults, setQuestionResults] = useState([]),
        [testResultsMap, setTestResultsMap] = useState({}),
        [adaptive, setAdaptive] = useState(false);

    const onGridReady = ()=>{
        // console.log('onGridReady');
        showHideUserRows();
        setLoading(false);
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    useEffect(()=>{
        if(timeTakenAggregate !== null){
            setReferenceLineXValue(timeTakenAggregate);
        }else if(selectedAverageTimeTaken){
            setReferenceLineXValue(selectedAverageTimeTaken);
        }

    },[timeTakenAggregate]);

    useEffect(()=>{
        if(scoreAverageAggregate !== null){
            setReferenceLineYValue(scoreAverageAggregate);
        }else if(selectedAverageScore !== null){
            setReferenceLineYValue(selectedAverageScore);
        }
    },[scoreAverageAggregate]);

    const onChartReady = ()=>{
        showHideUserRows();
        setLoading(false);
        // console.log('onChartReady')
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    const showHideUserRows = ()=>{
        let selectedData = [],
            selectedChartData = [];
        selectedData = unfilteredData.filter(row => studentIds.includes(row.studentId));
        selectedChartData = unfilteredChartData.filter(row => studentIds.includes(row.userId));

        const {averageGrowth, averageScore, averageTime} = calculateReferenceLineMeans(selectedChartData);

        const studentTimeFilteredData = [];

        if(averageGrowth && averageScore && averageTime){
            selectedChartData.forEach((row)=>{
                const addToChart = filterData(row, averageTime, averageScore);
                if(addToChart){
                    studentTimeFilteredData.push(row);
                }
            });
        }

        setGraphData(selectedData);
        if(averageTime && averageScore){
            setChartData(studentTimeFilteredData);
        }else{
            setChartData(selectedChartData);
        }
    }

    const filterData = (row, averageTime, averageScore)=>{
        let addToChart;
        switch (studentTimeFilter){
            case TIME_FILTER.ABOVE_AVERAGE_TIME_TAKEN.val:
                addToChart = row.timeTaken && row.timeTaken > averageTime;
                break;
            case TIME_FILTER.BELOW_AVERAGE_TIME_TAKEN.val:
                addToChart = row.timeTaken && row.timeTaken < averageTime;
                break;
            case TIME_FILTER.WITH_INCREASED_TIME_TAKEN.val:
                addToChart = row.timeDifference && row.timeDifference > 0;
                break;
            case TIME_FILTER.WITH_DECREASED_TIME_TAKEN.val:
                addToChart = row.timeDifference && row.timeDifference < 0;
                break;
            case TIME_FILTER.ABOVE_AVERAGE_SCORE.val:
                addToChart = row.value && row.value > averageScore;
                break;
            case TIME_FILTER.BELOW_AVERAGE_SCORE.val:
                addToChart = row.value && row.value < averageScore;
                break;
            case TIME_FILTER.WITH_INCREASED_SCORE.val:
                addToChart = row.difference && row.difference > 0;
                break;
            case TIME_FILTER.WITH_DECREASED_SCORE.val:
                addToChart = row.difference && row.difference < 0;
                break;
            default:
                addToChart = true;
                break;
        }
        return addToChart;
    }

    const calculateReferenceLineMeans = (dataArr, testType) =>{
        if (students.length === 0 || dataArr.length === 0 || Object.keys(studentSeriesMap).length === 0) {
            return {};
        }

        let growthPercentTotal = 0,
            preTestTotal = 0,
            postTestTotal = 0,
            preTestCount = 0,
            postTestCount = 0,
            timeTakenTotal = 0;

        dataArr.forEach(row =>{
            if(row.testType === TEST_GROUP_TEST_TYPES.PRE_TEST && studentSeriesMap[row.series].preTestAnswered){
                preTestTotal += row.value;
                preTestCount++;
                timeTakenTotal += row.timeTaken;
            }else if(row.testType === TEST_GROUP_TEST_TYPES.POST_TEST && studentSeriesMap[row.series].postTestAnswered){
                growthPercentTotal += row.difference;
                postTestTotal += row.value;
                postTestCount++;
                timeTakenTotal += row.timeTaken;
            }

            // bestScore = Math.max(bestScore,Math.round(row.value));
        });

        const timeTakenAverage = timeTakenTotal / (postTestCount+preTestCount),
            timeTakenInMins = Math.round(timeTakenAverage/60),//display in mins for chart
            averageScore = Math.round((preTestTotal+postTestTotal) / (postTestCount+preTestCount));

        if(timeTakenAggregate === null){
            setReferenceLineXValue(timeTakenInMins);
        }
        if(scoreAverageAggregate === null){
            setReferenceLineYValue(averageScore);
        }
        setSelectedAverageTimeTaken(timeTakenInMins);
        setSelectedAverageScore(averageScore);

        return {averageTime: timeTakenAverage, averageScore: averageScore, averageGrowth: Math.round(growthPercentTotal / postTestCount)}

    }

    useEffect(()=>{
        showHideUserRows();
    },[studentIds,studentTimeFilter]);

    useEffect(()=>{
        // console.log('before return');
        if(selectedTestIds === undefined || selectedTestIds.length === 0 || testAssignmentsData.length === 0){
            return;
        }
        // console.log('after return');
        setGraphData(null);
        setChartData(null);
        setLoading(true);

        if(testAssignmentsData.length > 0){
            let tempQuestionResults = [],
                tempTestResultsMap = {},
                isAdaptive = false;

            testAssignmentsData.forEach((testAssignmentData) => {
                let testType = testAssignmentData.testPurpose === 'DIAGNOSTIC_POST_TEST' ? TEST_GROUP_TEST_TYPES.POST_TEST : TEST_GROUP_TEST_TYPES.PRE_TEST;
                if(testAssignmentData.questionResultBeans != undefined) {
                    tempQuestionResults = [...tempQuestionResults, ...testAssignmentData.questionResultBeans];
                }

                testAssignmentData.testResultBeans.forEach((testResult) => {
                    if (!isAdaptive && testResult.adaptive) setAdaptive(true);
                    if (!tempTestResultsMap.hasOwnProperty(testResult.userId)) {
                        tempTestResultsMap[testResult.userId] = {preTest: null, postTest: null};
                    }

                    tempTestResultsMap[testResult.userId][testType] = testResult;
                });
            });

            setTestResultsMap(tempTestResultsMap);
            setQuestionResults(tempQuestionResults);
            setLoading(false);
        }else{
            setLoading(false);
        }
    },[triggerGenerate]);

    useEffect(()=>{
        if(questionResults.length > 0){
            setGraphData(null);
            setChartData(null);
            mapQuestionResults();
        }
    },[questionResults, selectedDifficultyIds]);

    const mapQuestionResults = ()=>{
        let studentResults = {};
        if(students.length === 0){
            return;
        }
        students.forEach(student=>{
            let preTestEntry = testResultsMap[student.userId][TEST_GROUP_TEST_TYPES.PRE_TEST],
                postTestEntry = testResultsMap[student.userId][TEST_GROUP_TEST_TYPES.POST_TEST],
                preTestObj = {
                    testMark: preTestEntry !== null ? preTestEntry.mark : 0,
                    testMaxMark: preTestEntry !== null ? preTestEntry.maxMark : 0,
                    testPercentVal: preTestEntry !== null ? preTestEntry.percentCorrect : 0,
                    timeTaken: preTestEntry !== null ? preTestEntry.timeTaken : 0,
                    answered: preTestEntry !== null && preTestEntry.completeDate !== undefined ? true: false
                },
                postTestObj = {
                    testMark: postTestEntry !== null ? postTestEntry.mark : 0,
                    testMaxMark: postTestEntry !== null ? postTestEntry.maxMark : 0,
                    testPercentVal: postTestEntry !== null ? postTestEntry.percentCorrect : 0,
                    timeTaken: postTestEntry !== null ? postTestEntry.timeTaken : 0,
                    answered: postTestEntry !== null && postTestEntry.completeDate !== undefined ? true: false
                }

            studentResults[student.userId] = {
                name:student.lastName+', '+student.firstName,
                initials:(student.lastName.substr(0,1)+student.firstName.substr(0,1)).toUpperCase(),
                preTest:preTestObj,
                postTest:postTestObj
            };
        });

        if (adaptive) {
            questionResults.forEach((questionResult,idx) =>{
                if(questionResult.testPurpose === 'DIAGNOSTIC_POST_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.POST_TEST);
                }else if(questionResult.testPurpose === 'DIAGNOSTIC_PRE_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.PRE_TEST);
                }
            });
        }

        let graphArr = [],
            chartArr = [],
            studentDifferenceMap = {};
        for (const [key, value] of Object.entries(studentResults)) {
            let rowData = {};
            rowData.studentId = parseInt(key);
            rowData.name = value.name;
            rowData.initials = value.initials;
            rowData.testIds = selectedTestIds;
            rowData.callback = openDialogCallback;

            let tablePrePercentVal,
                tablePostPercentVal,
                chartPrePercentVal = 0,
                chartPostPercentVal = 0,
                chartPreHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED,
                chartPostHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED;

            if (value.preTest.answered) {
                chartPrePercentVal = tablePrePercentVal = value.preTest.testPercentVal;
                chartPreHoverDisplay = value.preTest.testPercentVal + '%';
                rowData[preTestTimeTakenFieldName] = value.preTest.timeTaken;
            }
            if (value.postTest.answered) {
                chartPostPercentVal = tablePostPercentVal = value.postTest.testPercentVal;
                chartPostHoverDisplay = value.postTest.testPercentVal + '%';
                rowData[postTestTimeTakenFieldName] = value.postTest.timeTaken;
            }

            rowData[preTestPercentFieldName] = tablePrePercentVal;
            rowData[postTestPercentFieldName] = tablePostPercentVal;

            let differenceVal = 0,
                timeDifferenceVal = 0;
            studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId] = {
                differenceVal: null,
                timeDifferenceVal: null,
                preTestAnswered: value.preTest.answered,
                postTestAnswered: value.postTest.answered
            }
            if (!isNaN(rowData[postTestPercentFieldName]) && !isNaN(rowData[preTestPercentFieldName])) {
                differenceVal = Math.round(value.postTest.testPercentVal - value.preTest.testPercentVal);
                timeDifferenceVal = value.postTest.timeTaken - value.preTest.timeTaken
                studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId].differenceValue = differenceVal;
                studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId].timeDifferenceValue = timeDifferenceVal;
            }
            rowData.differenceVal = differenceVal;
            rowData.growthPercentage = differenceVal+'%';
            rowData.preTestAnswered = value.preTest.answered;
            rowData.postTestAnswered = value.postTest.answered;

            chartArr.push({
                userId: rowData.studentId,
                series: GRID_ENUMS.USER + rowData.studentId,
                name: rowData.name,
                test: preTest,
                testType: TEST_GROUP_TEST_TYPES.PRE_TEST,
                value: chartPrePercentVal,
                difference: differenceVal,
                timeDifference: timeDifferenceVal,
                testOrder: 0,
                hoverDisplay: chartPreHoverDisplay,
                answered: value.preTest.answered,
                timeTaken:value.preTest.timeTaken,
                label:rowData.initials
            });
            chartArr.push({
                userId: rowData.studentId,
                series: GRID_ENUMS.USER + rowData.studentId,
                name: rowData.name,
                test: postTest,
                testType: TEST_GROUP_TEST_TYPES.POST_TEST,
                value: chartPostPercentVal,
                difference: differenceVal,
                timeDifference: timeDifferenceVal,
                testOrder: 1,
                hoverDisplay: chartPostHoverDisplay,
                answered: value.postTest.answered,
                timeTaken:value.postTest.timeTaken,
                label:rowData.initials
            });
            graphArr.push(rowData);
        }
        //sort in order so pre Test shows before post test on the graph even if post test result comes first
        // chartArr.sort((a, b) =>{
        //     return a.testOrder - b.testOrder;
        // });


        setStudentSeriesMap(studentDifferenceMap);
        setChartData(chartArr);
        setUnfilteredChartData(chartArr);
        setGraphData(graphArr);
        setUnfilteredData(graphArr);
    }

    const addAdaptiveDataToStudent = (map,result,testType)=>{
        if(map.hasOwnProperty(result.userId) && map[result.userId].hasOwnProperty(testType)) {
            map[result.userId][testType].testMaxMark += result.adaptiveMaxMark !== 0 ? result.adaptiveMaxMark : result.maxMark;
            map[result.userId][testType].testPercentVal = calculateRoundPercent(map[result.userId][testType].testMark / map[result.userId][testType].testMaxMark);
        }
    }

    const exportColNames = {
        'preTestTimeTaken':'Pre/mid-test time taken (secs)',
        'postTestTimeTaken':'Post-test time taken (secs)',
    }

    const onExportCSV = useCallback(() => {
        gridRef.current.api.exportDataAsCsv(
            {fileName:REPORT_NAME,
                processHeaderCallback(params) {
                    if(exportColNames.hasOwnProperty(params.column.getColDef().field)){
                        return exportColNames[params.column.getColDef().field];
                    }else{
                        return params.column.getColDef().headerName;
                    }
                }
        });
    }, []);

    const onExportPDF = useCallback(() => {
        printDoc(defaultPDFParams,gridRef.current.api,REPORT_NAME);
    }, []);

    const getToolBar = () => (
        <div className="export-toolbar genBtn-blk">
            <button type="button" className="btn btn-default excel genBtn smallBtn"
                    onClick={onExportCSV}
            >Export CSV <FileDownloadIcon/>
            </button>
            <button type="button" className="btn btn-default reset genBtn smallBtn"
                    onClick={onExportPDF}
            >Export PDF <FileDownloadIcon/>
            </button>
        </div>
    );

    return(
        <div className="score-vs-time-view flexColumn">
            {!isLoading &&
                <>
                {(chartData !== null && chartData.length > 0) &&
                <div>
                    <LineChartLegend data={growthLegendData} showAsCard={true} cardColor={'#eeeeee'} direction={'horizontal'} style={{width: '20%', justifyContent: 'center'}}/>
                    <div style={{marginTop: '30px', height: '400px', display: 'flex', gap: '10px'}}>
                        <ScatterChartComponent
                            data={chartData}
                            xAxisRange={[0,80]}
                            xTickGap={5}
                            yAxisRange={[0,100]}
                            referenceLineXColor={referenceLineYColor}
                            referenceLineYColor={referenceLineXColor}
                            referenceLineXValue={referenceLineXValue}
                            referenceLineYValue={referenceLineYValue}
                            // lineXVisible={(showScoreLine && !hideAverageLine)}
                            // lineYVisible={(showTimeLine && !hideTimeTakenLine)}
                            lineXVisible={showScoreLine}
                            lineYVisible={showTimeLine}
                            labelDisplayMap={labelDisplayMap}
                            splitData={true}
                            xLabel={'Time taken to complete test (mins)'} yLabel={'Percent correct (%)'}/>
                    </div>
                </div>
            }
            {(chartData !== null && chartData.length === 0) &&
                <p className={'ch-dashboard-warning-text'}>No chart data available.</p>}
            {graphData != null &&
                <div className={'graph'}>
                        <div className={'data-set-header'}>
                            <h5>Data-set</h5> {getToolBar()}
                        </div>
                        <div className="ag-theme-balham ch-general-table-ag-grid"
                             style={{width:'100%', height: (rowHeight * graphData.length + headerHeight + 10) + 'px'}}>
                            <AgGridReact
                                style={{width:'100%'}}
                                ref={gridRef}
                                suppressPropertyNamesCheck={true}
                                suppressHorizontalScroll={true}
                                suppressRowTransform={true}
                                rowData={graphData}
                                columnDefs={columnDefs}
                                defaultColDef={COL_DEFS.defaultColDef}
                                onGridReady={onGridReady}
                                // onRowClicked={handleRowClick}
                                rowHeight={rowHeight}
                            />
                        </div>
                    </div>
                    }
                </>
            }
        </div>
    )
}
export default ScoreVsTime;
